import { getTimeZoneDate } from 'web-common/scripts/utils/DateHelper';
import FeatureToggleUtils from 'FeatureToggleUtils';
import BizzaboSnowplowTracker, {
    DISABLE_SNOWPLOW_TRACKING,
    SchemasHelper,
    ticketSchemaName,
    userSchemaName,
    sessionSchemaName,
    snowplowAppId,
    eventTypes
} from '@bizzabo/widget-analytics/dist/SnowplowTracker';
import config from 'config';
import { ticketsRegisteredSessionIdsSelector } from '../selectors/AgendaSelectors';
import { createWorkerFromFunction } from 'web-common/scripts/utils/WebWorkerUtils';
import {
    PROD_APP_ID,
    sessionCheck,
    TICKET_ENTITY_VALIDATION_LOG
} from '../constants/SnowplowConst';

export const isSnowplowTrackerEnabled = () => !FeatureToggleUtils.isFeatureEnabled(DISABLE_SNOWPLOW_TRACKING); // can be moved to a fe-core itself
const isSnowplowInDevLikeInProd = () => FeatureToggleUtils.isFeatureEnabled('snowplowInDevLikeInProd');

export const initializeSnowplowTrackerIfEnabled = () => {
    if (isSnowplowTrackerEnabled()) {
        BizzaboSnowplowTracker.dedicatedInitTracker({
            env: config.env,
            snowplowInDevLikeInProd: isSnowplowInDevLikeInProd(),
            appId: snowplowAppId(PROD_APP_ID, config.env),
            platform: 'web',
            bufferSize: 1
        });
        window.BizzaboSnowplowTracker = BizzaboSnowplowTracker;
    }
};

export const enableSnowplowTracking = () => BizzaboSnowplowTracker.enableAutomaticTemporaryTracking();

export const addSPSessionToGlobalContext = (session, eventTimeZone) => {
    if (isSnowplowTrackerEnabled()) {
        if (session.startDate) {
            session.startDateUTC = getTimeZoneDate(
                session.startDate, session.startMinute, eventTimeZone
            )?.format();
        }
        if (session.endDate) {
            session.endDateUTC = getTimeZoneDate(
                session.endDate, session.endMinute, eventTimeZone
            )?.format();
        }
        const sessionEntityData = [SchemasHelper.getSessionEntity(session)];
        // needs to prevent duplicating global context
        BizzaboSnowplowTracker.removeGlobalContext(sessionEntityData);
        BizzaboSnowplowTracker.setGlobalContext(sessionEntityData);
    }
};

export const removeGlobalSessionEntity = session => {
    if (isSnowplowTrackerEnabled() && session) {
        const sessionEntityData = [SchemasHelper.getSessionEntity(session)];
        BizzaboSnowplowTracker.removeGlobalContext(sessionEntityData);
    }
};

export const addSPAccountEventUserToGlobalContext = ( me, event ) => {
    if (isSnowplowTrackerEnabled()) {
        const data = [SchemasHelper.getAccountEntity(event), SchemasHelper.getEventEntity(event)];
        if (me?.id) {
            data.push(SchemasHelper.getUserEntity(me));
        }
        BizzaboSnowplowTracker.clearGlobalContext();
        me?.primaryEmail && BizzaboSnowplowTracker.setGlobalUserId(me.primaryEmail);
        BizzaboSnowplowTracker.setGlobalContext(data);
    }
};


let timer = null;

export const stopSessionHeartBeat = () => {
    if (timer && isSnowplowTrackerEnabled()) {
        clearInterval(timer);
        timer = null;
    }
};

const sendSessionPingEntity = () => {
    const sessionPingEntity = SchemasHelper.getSessionEventEntity();
    BizzaboSnowplowTracker.trackSelfDescribingEvent(sessionPingEntity);
};


export const startSessionHeartBeat = (delay) => {
    if (isSnowplowTrackerEnabled()) {
        stopSessionHeartBeat();
        timer = setInterval(sendSessionPingEntity, delay);
    }
};

export const snowplowSessionCheckStatus = (status, pagehideEvent) => {
    if (isSnowplowTrackerEnabled()) {
        if (status === sessionCheck.checkOut && !pagehideEvent) {
            sendSessionPingEntity();
        }
        const sessionAttendanceStatusEntity = SchemasHelper.getSessionAttendanceEntity(status);
        BizzaboSnowplowTracker.trackSelfDescribingEvent(sessionAttendanceStatusEntity);
        if (status === sessionCheck.checkIn) {
            sendSessionPingEntity();
        }
    }
};

export const addUserGlobalEntityStartTrack = me => {
    if (isSnowplowTrackerEnabled()) {
        if (me?.id) {
            const data = [SchemasHelper.getUserEntity(me)];
            me?.primaryEmail && BizzaboSnowplowTracker.setGlobalUserId(me.primaryEmail);
            BizzaboSnowplowTracker.setGlobalContext(data);
        }
        enableSnowplowTracking();
    }
};

export const addTicketToGlobalContext = (tickets = [], shouldCheckForSession) => {
    if (isSnowplowTrackerEnabled()) {
        if (tickets.length) {
            const virtualCheckinTickets = tickets.filter(({ virtualCheckin }) => !!virtualCheckin );
            if (virtualCheckinTickets.length) {
                const ticketEntity = [SchemasHelper.getTicketEntity(virtualCheckinTickets[0])];
                // needs to prevent duplicating global context
                BizzaboSnowplowTracker.removeGlobalContext(ticketEntity);
                BizzaboSnowplowTracker.setGlobalContext(ticketEntity);
            } else if (shouldCheckForSession) {
                sendSystemInfoEvent(TICKET_ENTITY_VALIDATION_LOG, 'addTicketToGlobalContext no ticket with virtualCheckin');
            }
        } else if (shouldCheckForSession) {
            sendSystemInfoEvent(TICKET_ENTITY_VALIDATION_LOG, 'addTicketToGlobalContext no tickets received');
        }
    }
};

export const startSPTrackUser = (shouldCheckForUser = false) => {
    if (isSnowplowTrackerEnabled()) {
        BizzaboSnowplowTracker.checkFlagVsSchemaStartTrack(shouldCheckForUser, userSchemaName);
    }
};
export const startSPTicketTrack = shouldCheckForSession => {
    if (isSnowplowTrackerEnabled()) {
        BizzaboSnowplowTracker.checkFlagVsSchemaStartTrack(shouldCheckForSession, sessionSchemaName);
    }
};

export const startSPSessionTrack = (shouldCheckForTicket = false) => {
    if (isSnowplowTrackerEnabled()) {
        BizzaboSnowplowTracker.checkSchemaVsFlagStartTrack(shouldCheckForTicket, ticketSchemaName);
    }
};

export const sendSnowplowEventByType = (snowplowEvent) => {
    if (snowplowEvent?.eventType === eventTypes.BUTTON_CLICK && snowplowEvent.eventData) {
        const { buttonType, subCategory } = snowplowEvent.eventData;
        sendCustomClick(buttonType, subCategory);
    }
    if (snowplowEvent?.eventType === eventTypes.SYSTEM_INFO && snowplowEvent.eventData) {
        const { systemName, actionDescription } = snowplowEvent.eventData;
        sendSystemInfoEvent(systemName, actionDescription);
    }
};

const safeStringify = (data) => {
    try {
        return JSON.stringify(data);
    } catch (e) {
        return '';
    }
};

export const sendCustomClick = (buttonType, subCategory, refOrElem, description, action) => {
    if (!isSnowplowTrackerEnabled()) {
        return;
    }

    const element =
        (refOrElem?.current?.getBoundingClientRect && refOrElem.current) ||
        (refOrElem?.getBoundingClientRect && refOrElem);
    const { top: positionY = null, left: positionX = null } = element?.getBoundingClientRect?.() || {};
    sendCustomClickByPosition(buttonType, subCategory, { positionX, positionY }, description, action);
};

export const sendCustomClickByPosition = (buttonType, subCategory, { positionX, positionY }, description, action) => {
    if (!isSnowplowTrackerEnabled()) {
        return;
    }

    const clickEntity = SchemasHelper.getClickEventEntity({
        buttonType,
        subCategory,
        positionX,
        positionY,
        description: safeStringify(description),
        action: action,
    });
    BizzaboSnowplowTracker.trackSelfDescribingEvent(clickEntity);
};

export const sendSystemInfoEvent = (systemName, actionDescription) => {
    if (isSnowplowTrackerEnabled() && systemName) {
        const systemEntity = SchemasHelper.getSystemInfoEventEntity({
            systemName,
            actionDescription
        });
        BizzaboSnowplowTracker.trackSelfDescribingEvent(systemEntity);
    }
};

export const trackCrossOriginTab = (tab, ms = 1000) => {
    const worker = createWorkerFromFunction(intervalWorker);
    return new Promise(resolve => {
        worker.addEventListener('message', event => {
            if (event.data === 'ping') {
                if (!tab || tab.closed) {
                    worker.terminate();
                    resolve(true);
                }
            }
        });
        worker.postMessage({ start: true, ms });
    });
};

const intervalWorker = () => {
    self.addEventListener('message', event => {
        if (event.data.start) {
            setInterval(() => {
                self.postMessage('ping');
            }, event.data.ms);
        }
    });
};

export const trackSystemEntityEvent = (systemName, actionDescription) => {
    if (isSnowplowTrackerEnabled() && systemName) {
        const systemEntity = SchemasHelper.getSystemInfoEventEntity({
            systemName,
            actionDescription
        });
        BizzaboSnowplowTracker.sendSystemWithPageViewCheck(systemEntity);
    }
};
